import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import Badge from "../../components/ui-components/Badge";
import FilterDropdown from "../../components/ui-components/FilterDropdown";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import { toast } from "../../components/ui-components/Toast.jsx";
import { listBoardReports, listBoardReportTemplates, createRun } from "./document/boardReportApi.js";
import { formatPeriodLabel } from "./document/formatPeriod.js";

// Fallback labels for report types the template catalog didn't resolve (e.g. it
// failed to load). The catalog (listBoardReportTemplates) is the primary source.
const TYPE_LABELS = { monthly_summary: "Monthly Summary" };

/**
 * BoardReportList - lists configured board reports (/board-reports). Each row
 * links to its home (Overview) and offers a per-row "New run"; the primary
 * "New board report" action lives in the app header. A search box and a type
 * filter on the table card narrow the list (both client-side), matching the
 * budgets list page.
 */
const BoardReportList = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingRunId, setCreatingRunId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await listBoardReports();
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Load the template catalog purely for type -> label resolution (the type filter
  // and the Type column). Best-effort: on failure we fall back to TYPE_LABELS.
  useEffect(() => {
    listBoardReportTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  const typeLabel = useCallback(
    (value) => {
      const fromCatalog = templates.find((t) => t?.type === value)?.name;
      return fromCatalog || TYPE_LABELS[value] || value || "—";
    },
    [templates]
  );

  const handleCreateRun = useCallback(
    async (report) => {
      setCreatingRunId(report.id);
      try {
        const run = await createRun(report.id);
        navigate(`/board-reports/${report.id}/runs/${run.id}/review`);
      } catch (err) {
        toast.error("Failed to create run");
      } finally {
        setCreatingRunId(null);
      }
    },
    [navigate]
  );

  // Type filter options — only the distinct types that actually exist in the list,
  // prefixed with an "All types" option.
  const typeOptions = useMemo(() => {
    const seen = new Map();
    for (const r of reports) {
      const v = r.type;
      if (v == null || v === "") continue;
      if (!seen.has(v)) seen.set(v, typeLabel(v));
    }
    return [{ value: "all", label: "All types" }, ...[...seen].map(([value, label]) => ({ value: String(value), label }))];
  }, [reports, typeLabel]);

  const search = searchQuery.trim();
  const searchLower = search.toLowerCase();
  const filteredReports = useMemo(
    () =>
      reports.filter((r) => {
        if (typeFilter !== "all" && String(r.type || "") !== typeFilter) return false;
        if (searchLower) {
          const haystack = [r.name, typeLabel(r.type), formatPeriodLabel(r.period)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(searchLower)) return false;
        }
        return true;
      }),
    [reports, typeFilter, searchLower, typeLabel]
  );

  const columnDefs = useMemo(
    () => [
      {
        accessor: "name",
        Header: () => <span>Name</span>,
        Cell: ({ row }) => (
          <span className="font-medium text-text truncate">{row.original.name || "Untitled"}</span>
        ),
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "type",
        Header: () => <span>Type</span>,
        Cell: ({ value }) => <Badge status="info" label={typeLabel(value)} />,
        width: 170,
        minWidth: 140,
        maxWidth: 200,
      },
      {
        accessor: "period",
        Header: () => <span>Period</span>,
        Cell: ({ value }) => <span className="text-sm text-text-2">{formatPeriodLabel(value)}</span>,
        width: 150,
        minWidth: 120,
        maxWidth: 190,
      },
      {
        id: "actions",
        align: "right",
        Header: () => null,
        // The row opens the report home (Overview); everything else — content, runs, delivery — is a
        // tab in there. The one shortcut worth surfacing on the list is generating a run, so that
        // stays as a primary per-row action.
        Cell: ({ row }) => {
          const report = row.original;
          const isCreating = creatingRunId === report.id;
          return (
            <div className="flex items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className={secondaryButtonCls}
                onClick={() => handleCreateRun(report)}
                disabled={isCreating}
              >
                {isCreating ? "Creating…" : "New run"}
              </button>
            </div>
          );
        },
        width: 160,
        minWidth: 140,
      },
    ],
    [creatingRunId, handleCreateRun, typeLabel]
  );

  const matchCount = filteredReports.length;
  const noun = matchCount === 1 ? "board report" : "board reports";
  const hint = isLoading
    ? "Loading…"
    : search
      ? `${matchCount} ${noun} matching "${search}"`
      : `Showing ${matchCount} ${noun}`;

  return (
    <Fragment>
      <AdministrationHeader title="Board Reports" showPeriodSelector={false} />

      {/* New board report is the page's primary action, in the row-1 header cluster. */}
      <HeaderPortal slot="actions">
        <button
          type="button"
          onClick={() => navigate("/board-reports/new/config")}
          className={primaryButtonCls}
        >
          <FontAwesomeIcon icon={icon({ name: "plus", style: "solid" })} className="mr-1.5 text-[12px]" />
          New board report
        </button>
      </HeaderPortal>

      <div className="w-full p-8">
        {/* Search + type filter sit on the card's first row; the shown count sits
            below them on the second row (untitled TableCard layout, like /budgets). */}
        <TableCard
          subtitle={hint}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search board reports…"
          controls={
            typeOptions.length > 1 ? (
              <FilterDropdown
                ariaLabel="Filter by type"
                prefix="Type:"
                value={typeFilter}
                onChange={setTypeFilter}
                options={typeOptions}
                minWidth={140}
                height={36}
              />
            ) : null
          }
          error={error}
          errorMessage="Error loading board reports"
          onRetry={load}
        >
          {/* No loading spinner here (isLoading intentionally not passed to TableCard) - the "Loading…"
              subtitle conveys the fetch. While loading we render nothing rather than flashing the
              empty state. */}
          {isLoading ? null : reports.length === 0 ? (
            <EmptyState
              icon={icon({ name: "file-chart-column", style: "light" })}
              title="No board reports yet"
              message="Create a board report to generate board-ready monthly summaries."
              action={{
                label: "Create board report",
                onClick: () => navigate("/board-reports/new/config"),
                icon: icon({ name: "plus", style: "regular" }),
              }}
            />
          ) : filteredReports.length === 0 ? (
            <EmptyState
              icon={icon({ name: "magnifying-glass" })}
              title="No matching board reports"
              message={search ? `No board reports match "${search}".` : "No board reports match the current filter."}
              action={{
                label: "Clear search",
                onClick: () => {
                  setSearchQuery("");
                  setTypeFilter("all");
                },
                icon: icon({ name: "xmark", style: "regular" }),
                variant: "secondary",
              }}
            />
          ) : (
            <FlexTable
              columns={columnDefs}
              data={filteredReports}
              getRowId={(r) => r.id}
              onRowClick={(id) => navigate(`/board-reports/${id}`)}
              rowAriaLabel={(r) => `Open board report ${r.name}`}
            />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};

export default BoardReportList;
